// Rolling-Tubes decor for Level 4: five solid platforms connected by long
// rectangular bridges with a "sliding floor" that drags the player sideways
// (treadmill mechanic). The original spec was a rotating cylinder, but the
// curved top surface made the player jitter and the spinning chevron texture
// became unreadable — a flat square + scrolling texture reads cleanly and
// keeps physics predictable while still creating the carry-along challenge.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";

const PLATFORM_COLOR = 0xff5fa7;   // pink (Fall Guys candy palette)
const PLATFORM_RIM = 0xffd166;     // gold trim
const TUBE_COLOR = 0x4cc9ff;       // bright blue
const TUBE_BAND = 0xffffff;        // white directional chevrons

export interface RollingTubesHandle {
  group: THREE.Group;
  /** Solid platform meshes — handed to the standable-surface raycaster so the
   *  player rides them normally. Tubes are NOT in here; tube support is
   *  handled by hasTubeSupport. */
  supportMeshes: THREE.Object3D[];
  /** s-positions for each platform center (used by Spawner to mount gates). */
  gatePositions: number[];
  /** Per-frame tick — call with clockMs (Date.now()-origin) so rotation phase
   *  is identical on every client in multiplayer. */
  tick: (clockMs: number) => void;
  /** Swinging-axe pendulum contact for the player at (s, lateral, worldY).
   *  Same shape as circleSpinner.barContact / mazeRush.pendulumContact so
   *  Game can reuse the knockback impulse path. */
  axeContact: (s: number, l: number, worldY: number, radius: number) => {
    normalL: number;
    normalY: number;
    penetration: number;
    barVelL: number;
  } | null;
  /** Forward HARD-block at any axe the player hasn't cleared by jumping
   *  high enough. Returns the s-coordinate to clamp the player to (just
   *  before the axe blade) or null if no axe is currently blocking them. */
  axeForwardBlock: (s: number, worldY: number) => number | null;
  /** Horizontal spinner bar on each moving section — same shape as the
   *  circle-spinner barContact so Game can reuse the impulse path. */
  barContact: (s: number, l: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    barVelS: number;
    barVelL: number;
  } | null;
  /** Returns the tube the player is currently above, or null. Game uses the
   *  returned tube to apply lateral surface velocity (rolling push) and to
   *  enforce a max lateral offset before the player falls off the side. */
  tubeAt: (s: number) => {
    radius: number;
    angularVel: number;
    /** Half-width on top of the tube where the player can still stand. Past
     *  this, slide off into a fall. */
    topHalfWidth: number;
  } | null;
  /** Pyramid cannonball vs player. Returns the first ball whose 3-D
   *  sphere overlaps the player capsule, with a knock direction (along
   *  -s, since balls roll down the pyramid) so Game.ts can apply a
   *  knockdown impulse and consume the ball. Returns null when nothing
   *  is hit. Player feet sit at worldY; treat the capsule as a 1.6m-tall
   *  cylinder centred 0.55m above feet. */
  ballContact: (s: number, l: number, worldY: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    consume: () => void;
  } | null;
}

/** Platforms in this mode are chunky pedestals — top sits exactly at the
 *  tube radius so the player steps cleanly between platforms and tubes. */
function buildPlatform(width: number, depth: number, topY: number): THREE.Group {
  const g = new THREE.Group();
  // Pyramid-style chunky stone block — full-height cube extending from
  // ground (y=0) up to the platform's top Y. The whole stack of these
  // up the course gives the classic ziggurat silhouette.
  const thickness = Math.max(1.2, topY);
  const block = new THREE.Mesh(
    new SoftBox(width, thickness, depth),
    new THREE.MeshStandardMaterial({
      color: 0xe9c08b,
      emissive: 0xb27a3b,
      emissiveIntensity: 0.06,
      roughness: 0.7,
    }),
  );
  block.position.y = topY - thickness / 2;
  block.receiveShadow = true;
  block.castShadow = true;
  g.add(block);
  // Darker base trim at the bottom edge for visual layering.
  const baseTrim = new THREE.Mesh(
    new SoftBox(width + 0.3, 0.4, depth + 0.3),
    new THREE.MeshStandardMaterial({ color: 0xa66f3a, roughness: 0.65 }),
  );
  baseTrim.position.y = topY - thickness + 0.2;
  g.add(baseTrim);
  // Gold rim along the top edge so the walkable surface is unmistakable.
  const rim = new THREE.Mesh(
    new SoftBox(width + 0.18, 0.14, depth + 0.18),
    new THREE.MeshStandardMaterial({ color: PLATFORM_RIM, roughness: 0.4 }),
  );
  rim.position.y = topY + 0.07;
  g.add(rim);
  return g;
}

/** Treadmill texture: blue base with darker stripes + sideways chevron arrows
 *  showing the surface slide direction. Animated by scrolling `offset.x`
 *  along the lateral (U) axis — chevrons stay readable because the texture
 *  itself moves, not the geometry. */
let _treadmillTexture: THREE.CanvasTexture | null = null;
function treadmillTexture(): THREE.CanvasTexture {
  if (_treadmillTexture) return _treadmillTexture;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = `#${TUBE_COLOR.toString(16).padStart(6, "0")}`;
  ctx.fillRect(0, 0, 256, 256);
  // Darker bands along the slide axis (horizontal stripes).
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  for (let y = 0; y < 256; y += 64) ctx.fillRect(0, y, 256, 32);
  // White sideways-pointing chevrons (along U axis). Two columns so the
  // pattern reads well at any repeat.
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  for (let y = 16; y < 256; y += 64) {
    for (const x of [60, 180]) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 36, y + 16);
      ctx.lineTo(x, y + 32);
      ctx.lineTo(x + 10, y + 16);
      ctx.closePath();
      ctx.fill();
    }
  }
  _treadmillTexture = new THREE.CanvasTexture(canvas);
  _treadmillTexture.wrapS = THREE.RepeatWrapping;
  _treadmillTexture.wrapT = THREE.RepeatWrapping;
  _treadmillTexture.colorSpace = THREE.SRGBColorSpace;
  return _treadmillTexture;
}

interface TubeMesh {
  /** The flat top "treadmill" surface — receives the scrolling texture. */
  topMesh: THREE.Mesh;
  /** Texture for the top, scrolled each frame to look like the floor slides. */
  topTexture: THREE.Texture;
  /** Surface speed (m/s) at the floor — positive = slide toward +X (right). */
  surfaceVel: number;
}

export function buildRollingTubesCourse(path: Path): RollingTubesHandle {
  const rt = path.rollingTubes;
  if (!rt) throw new Error("buildRollingTubesCourse called on non-rolling-tubes path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const trackWidth = rt.halfWidth * 2;
  const gatePositions: number[] = [];
  const axes: {
    pivot: THREE.Group;
    pivotY: number; pivotS: number;
    rope: number;
    bladeHalfWidth: number; bladeHalfHeight: number;
    amp: number; freq: number; phase: number; angle: number;
  }[] = [];
  interface SpinnerEntry {
    s: number;
    halfLength: number;
    thickness: number;
    angularVel: number;
    phase0: number;
    angle: number;
    pivot: THREE.Group;
  }
  const spinners: SpinnerEntry[] = [];
  // Platforms — every platform after the start pad gets a gate at its center.
  for (let i = 0; i < rt.platforms.length; i++) {
    const p = rt.platforms[i]!;
    const len = p.end - p.start;
    if (len <= 0.5) continue;
    const platform = buildPlatform(trackWidth, len, p.y);
    platform.position.set(0, 0, p.start + len / 2);
    group.add(platform);
    supportMeshes.push(...platform.children.filter((c) => c instanceof THREE.Mesh));
  }
  // Spread the 5 question gates EVENLY across the climbable steps so
  // one gate sits on roughly every 3rd platform. With 14 platforms,
  // interior climb steps are 1..12 — pick 5 evenly-spaced indices.
  {
    const interior = rt.platforms.length - 2; // exclude start + summit
    for (let g = 0; g < 5; g++) {
      const t = g / 4; // 0..1 across the 5 gates
      const idx = Math.max(1, Math.min(interior, Math.round(1 + t * (interior - 1))));
      const plat = rt.platforms[idx]!;
      gatePositions.push((plat.start + plat.end) / 2);
    }
  }

  // ── Pyramid spinners — scattered on the middle steps. Each one
  //    sits in the centre of its step; angular vel and direction
  //    randomised per index so consecutive steps don't all rotate
  //    the same way. Skip the first + last platforms (start + summit). ──
  for (let i = 1; i < rt.platforms.length - 1; i++) {
    const p = rt.platforms[i]!;
    // Place a spinner on roughly every-other middle step so the
    // climb has a rhythm of "safe → spinner → safe".
    if (i % 2 === 0) continue;
    const spinnerHalfLen = trackWidth * 0.5;
    const spinnerThickness = 0.34;
    const spinnerY = p.y + 0.85;
    const spinnerS = (p.start + p.end) / 2;
    const spinnerVel = (i % 2 === 0 ? 1 : -1) * (1.2 + (i % 3) * 0.2);
    const spinnerPivot = new THREE.Group();
    spinnerPivot.position.set(0, spinnerY, spinnerS);
    const armMat = new THREE.MeshStandardMaterial({
      color: 0xff2ca4, emissive: 0xff2ca4, emissiveIntensity: 0.2, roughness: 0.4,
    });
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(spinnerThickness, spinnerHalfLen * 2, 12, 24),
      armMat,
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    spinnerPivot.add(arm);
    const tipMat = new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.4, roughness: 0.3 });
    for (const x of [-spinnerHalfLen, spinnerHalfLen]) {
      const tip = new THREE.Mesh(new THREE.SphereGeometry(spinnerThickness * 1.2, 14, 12), tipMat);
      tip.position.x = x;
      spinnerPivot.add(tip);
    }
    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.5, 0.45, 18),
      new THREE.MeshStandardMaterial({ color: 0xa855f7, roughness: 0.45 }),
    );
    hub.position.y = -0.24;
    spinnerPivot.add(hub);
    group.add(spinnerPivot);
    spinners.push({
      s: spinnerS,
      halfLength: spinnerHalfLen,
      thickness: spinnerThickness,
      angularVel: spinnerVel,
      phase0: (i * 1.83) % (Math.PI * 2),
      angle: 0,
      pivot: spinnerPivot,
    });
  }

  // ── Top cannon — sits on the summit and fires a continuous stream
  //    of balls that bounce DOWN the pyramid steps. Players have to
  //    weave around them on the climb. ──
  const topPlatform = rt.platforms[rt.platforms.length - 1]!;
  const topY = topPlatform.y;
  const topZ = (topPlatform.start + topPlatform.end) / 2;
  {
    const cannonBase = new THREE.Mesh(
      new THREE.CylinderGeometry(0.9, 1.1, 0.6, 16),
      new THREE.MeshStandardMaterial({ color: 0x8b5cf6, roughness: 0.5 }),
    );
    cannonBase.position.set(0, topY + 0.3, topZ);
    group.add(cannonBase);
    const cannonBarrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.5, 2.2, 16),
      new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.4 }),
    );
    cannonBarrel.position.set(0, topY + 1.4, topZ);
    cannonBarrel.rotation.x = -Math.PI * 0.32; // tilt down the pyramid
    group.add(cannonBarrel);
    // Finish-line arch at the very END of the summit — right where
    // the path's finish trigger fires. Sits BEHIND the cannon (past
    // it on the path) so the player walks the full top step then
    // crosses the chequered strip to complete the level.
    const finishZ = topPlatform.end - 0.4;
    const archMat = new THREE.MeshStandardMaterial({
      color: 0xffd166, emissive: 0xff8a3d, emissiveIntensity: 0.35, roughness: 0.4,
    });
    for (const sx of [-1, 1] as const) {
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.5, 0.6, 8, 16),
        archMat,
      );
      post.position.set(sx * (trackWidth / 2 + 0.6), topY + 4, finishZ);
      post.castShadow = true;
      group.add(post);
    }
    const beam = new THREE.Mesh(
      new SoftBox(trackWidth + 2.4, 1.6, 0.8),
      archMat,
    );
    beam.position.set(0, topY + 8, finishZ);
    group.add(beam);
    const badge = new THREE.Mesh(
      new SoftBox(5.4, 1.6, 0.5),
      new THREE.MeshStandardMaterial({ color: 0xff5fa7, emissive: 0xff5fa7, emissiveIntensity: 0.6 }),
    );
    badge.position.set(0, topY + 9.2, finishZ - 0.1);
    group.add(badge);
    // Checkered chequer strip across the top step at the finish s.
    const checkMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45 });
    const checkDark = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.45 });
    const tiles = 8;
    for (let i = 0; i < tiles; i++) {
      const tile = new THREE.Mesh(
        new SoftBox((trackWidth + 1.0) / tiles, 0.05, 1.2),
        i % 2 === 0 ? checkMat : checkDark,
      );
      tile.position.set(
        -trackWidth / 2 - 0.5 + ((trackWidth + 1.0) / tiles) * (i + 0.5),
        topY + 0.06,
        finishZ - 0.6,
      );
      group.add(tile);
    }
  }

  // Falling balls — independent physics objects. They spawn at the
  // top and roll/bounce down toward s = 0. Recycled when they fly off.
  interface PyramidBall {
    mesh: THREE.Mesh;
    vy: number;
    vs: number; // forward velocity (-Z direction, down the pyramid)
    vl: number; // lateral drift
    s: number;
    l: number;
    y: number;
    radius: number;
  }
  const balls: PyramidBall[] = [];
  const ballGeo = new THREE.SphereGeometry(0.55, 18, 14);
  const ballMats = [0xff5fa7, 0xffd166, 0x6bd0ff, 0x6be09b].map((c) =>
    new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.18, roughness: 0.35 }),
  );
  let nextBallSpawnMs = 1500;
  const BALL_SPAWN_INTERVAL = 1100;
  const spawnBall = () => {
    const mat = ballMats[balls.length % ballMats.length]!;
    const mesh = new THREE.Mesh(ballGeo, mat);
    mesh.castShadow = true;
    const lat = (Math.random() - 0.5) * (rt.halfWidth * 1.4);
    const b: PyramidBall = {
      mesh,
      vy: 0,
      vs: -6 - Math.random() * 2,
      vl: (Math.random() - 0.5) * 1.5,
      s: topZ - 1.2,
      l: lat,
      y: topY + 1.2,
      radius: 0.55,
    };
    mesh.position.set(b.l, b.y, b.s);
    group.add(mesh);
    balls.push(b);
  };

  // Tubes — now flat "treadmill" bridges: a top surface with a scrolling
  // texture + two angled side walls that read as a chunky pillar from any
  // camera angle. Width of the walkable top = 2 × halfWidth (set on the
  // tube data; equal to track width).
  const tubeMeshes: TubeMesh[] = [];
  for (let i = 0; i < rt.tubes.length; i++) {
    const t = rt.tubes[i]!;
    const len = t.end - t.start;
    const topWidth = rt.halfWidth * 1.6; // a bit narrower than the platforms
    const topThickness = 0.5;
    // Per-tube surface midpoint + slope. The previous decor assumed a
    // single flat `surfaceY`; tubes now connect alternating high/low
    // platforms so each one has its own midY + tilt.
    const surfaceY = (t.startY + t.endY) / 2;
    // rotation.x = +angle tilts +Z end down; we want the end nearer
    // courseEnd to drop to endY, so angle = atan(startY - endY / len).
    const slopeAngle = Math.atan2(t.startY - t.endY, len);

    // Big swinging axe in the MIDDLE of every moving tube. Blade spans the
    // entire walkable width so the player has to time their run across.
    const axePivotY = surfaceY + 5.4;
    const axePivotS = t.start + len / 2;
    const axePivot = buildAxePendulum({
      rope: 3.4,
      bladeWidth: topWidth * 0.96,
      bladeHeight: 1.9,
    });
    axePivot.position.set(0, axePivotY, axePivotS);
    group.add(axePivot);
    group.add(buildAxeGantry(axePivotY, axePivotS, trackWidth));
    axes.push({
      pivot: axePivot,
      pivotY: axePivotY,
      pivotS: axePivotS,
      rope: 3.4,
      bladeHalfWidth: topWidth * 0.48,
      bladeHalfHeight: 0.95,
      amp: 1.05,
      freq: 1.9 + (i % 2 === 0 ? 0 : 0.25), // quicker swing than before
      phase: i * 1.3,
      angle: 0,
    });

    // ── Spinner bar near the foot of each tube, between axe and end ──
    // Low rotating bar (~0.8m above the tube top). Player must jump it.
    // Direction alternates and is offset from the axe phase so the two
    // hazards don't overlap.
    const spinnerHalfLen = topWidth * 0.5;
    const spinnerThickness = 0.34;
    const spinnerY = surfaceY + 0.85;
    const spinnerS = t.start + len * 0.78;
    const spinnerVel = (i % 2 === 0 ? 1 : -1) * (1.4 + (i % 3) * 0.18);
    const spinnerPivot = new THREE.Group();
    spinnerPivot.position.set(0, spinnerY, spinnerS);
    const armMat = new THREE.MeshStandardMaterial({
      color: 0xff2ca4, emissive: 0xff2ca4, emissiveIntensity: 0.18, roughness: 0.4,
    });
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(spinnerThickness, spinnerHalfLen * 2, 12, 24),
      armMat,
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    spinnerPivot.add(arm);
    const tipMat = new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.4, roughness: 0.3 });
    for (const x of [-spinnerHalfLen, spinnerHalfLen]) {
      const tip = new THREE.Mesh(new THREE.SphereGeometry(spinnerThickness * 1.2, 14, 12), tipMat);
      tip.position.x = x;
      spinnerPivot.add(tip);
    }
    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.5, 0.45, 18),
      new THREE.MeshStandardMaterial({ color: 0xa855f7, roughness: 0.45 }),
    );
    hub.position.y = -0.24;
    spinnerPivot.add(hub);
    group.add(spinnerPivot);
    spinners.push({
      s: spinnerS,
      halfLength: spinnerHalfLen,
      thickness: spinnerThickness,
      angularVel: spinnerVel,
      phase0: (i * 1.83) % (Math.PI * 2),
      angle: 0,
      pivot: spinnerPivot,
    });

    // Top walkable plate — receives the scrolling texture.
    const topTex = treadmillTexture().clone();
    topTex.wrapS = THREE.RepeatWrapping;
    topTex.wrapT = THREE.RepeatWrapping;
    // Repeat the texture across the long axis so chevrons are dense enough
    // to read; only one repeat across the width.
    topTex.repeat.set(1, Math.max(1, Math.round(len / 5)));
    // For tubes that slide LEFT, mirror the texture along U so the chevrons
    // drawn pointing right end up pointing left — matching the actual
    // surface direction. Pair this with the offset-sign fix below in tick().
    if (t.angularVel < 0) topTex.repeat.x = -1;
    topTex.needsUpdate = true;
    const topMesh = new THREE.Mesh(
      new SoftBox(topWidth, topThickness, len),
      new THREE.MeshStandardMaterial({
        map: topTex,
        roughness: 0.55,
        metalness: 0.0,
      }),
    );
    topMesh.position.set(0, surfaceY - topThickness / 2, t.start + len / 2);
    topMesh.rotation.x = slopeAngle;
    topMesh.castShadow = true;
    topMesh.receiveShadow = true;
    group.add(topMesh);
    // Slime sheen on slide tubes — green-blue surface so the player
    // recognises them as slippery.
    if (t.slide) {
      const slimeMat = topMesh.material as THREE.MeshStandardMaterial;
      slimeMat.color.setHex(0x6be09b);
      slimeMat.emissive = new THREE.Color(0x12cf63);
      slimeMat.emissiveIntensity = 0.25;
      slimeMat.metalness = 0.18;
      slimeMat.roughness = 0.2;
    }

    // Decorative side walls beneath the top so the bridge has visual depth
    // and reads as a "pillar with a sliding top" rather than a thin slab.
    const wallMat = new THREE.MeshStandardMaterial({ color: TUBE_COLOR, roughness: 0.55 });
    const wallHeight = surfaceY * 0.9;
    const wallThickness = 0.55;
    for (const sign of [-1, 1] as const) {
      const wall = new THREE.Mesh(
        new SoftBox(wallThickness, wallHeight, len),
        wallMat,
      );
      wall.position.set(sign * (topWidth / 2 - wallThickness / 2), surfaceY - topThickness - wallHeight / 2, t.start + len / 2);
      wall.rotation.x = slopeAngle;
      group.add(wall);
    }
    // Gold trim along the top edges so the walkable band is obvious.
    const trimMat = new THREE.MeshStandardMaterial({ color: PLATFORM_RIM, roughness: 0.4 });
    for (const sign of [-1, 1] as const) {
      const trim = new THREE.Mesh(
        new SoftBox(0.16, 0.16, len),
        trimMat,
      );
      trim.position.set(sign * (topWidth / 2 - 0.05), surfaceY + 0.05, t.start + len / 2);
      trim.rotation.x = slopeAngle;
      group.add(trim);
    }

    // Surface velocity: the original angularVel × radius gives a consistent
    // m/s number so the existing carry mechanic in Game keeps working.
    const surfaceVel = t.angularVel * t.radius;
    tubeMeshes.push({ topMesh, topTexture: topTex, surfaceVel });
  }

  let lastClockMs = 0;
  const tick = (clockMs: number) => {
    const dt = lastClockMs === 0 ? 1 / 60 : Math.max(0, Math.min(0.064, (clockMs - lastClockMs) / 1000));
    lastClockMs = clockMs;
    const tSec = clockMs / 1000;
    for (const tm of tubeMeshes) {
      tm.topTexture.offset.x = (-(tm.surfaceVel * tSec) / 5) % 1;
    }
    for (const a of axes) {
      a.angle = a.amp * Math.sin(tSec * a.freq + a.phase);
      a.pivot.rotation.z = a.angle;
    }
    for (const sp of spinners) {
      sp.angle = sp.phase0 + tSec * sp.angularVel;
      sp.pivot.rotation.y = sp.angle;
    }
    // ── Pyramid balls ──
    if (clockMs >= nextBallSpawnMs && balls.length < 12) {
      spawnBall();
      nextBallSpawnMs = clockMs + BALL_SPAWN_INTERVAL;
    }
    for (let i = balls.length - 1; i >= 0; i--) {
      const b = balls[i]!;
      // Gravity + lateral drift.
      b.vy -= 22 * dt;
      b.y += b.vy * dt;
      b.s += b.vs * dt;
      b.l += b.vl * dt;
      // Find the step Y the ball is currently above to bounce off it.
      let stepY = 0;
      for (const p of rt.platforms) {
        if (b.s >= p.start && b.s <= p.end) { stepY = p.y; break; }
      }
      const restY = stepY + b.radius;
      if (b.y <= restY && b.vy < 0) {
        // Bounce — lose some energy, gain a little forward kick so they
        // keep rolling down the pyramid.
        b.y = restY;
        b.vy = -b.vy * 0.55 + 2.5;
        b.vs -= 0.8;
      }
      b.mesh.position.set(b.l, b.y, b.s);
      // Recycle once the ball passes the start or drifts way off-track.
      if (b.s < -4 || Math.abs(b.l) > rt.halfWidth + 6 || b.y < -8) {
        group.remove(b.mesh);
        balls.splice(i, 1);
      }
    }
  };

  /** Pyramid cannonball vs player capsule. Compares ball centre (in s/l/y)
   *  against the player capsule (≈1.6m tall, centred at worldY+0.55).
   *  Returns the first overlap with a knock direction + a consume() to
   *  spend the ball so it doesn't slam the player multiple frames in a
   *  row. */
  const ballContact = (
    s: number,
    l: number,
    worldY: number,
    radius: number,
  ) => {
    const PLAYER_HEIGHT = 1.6;
    const pyMin = worldY;
    const pyMax = worldY + PLAYER_HEIGHT;
    for (const b of balls) {
      const ds = b.s - s;
      const dl = b.l - l;
      // Vertical: clamp ball centre to the player capsule's y-extent.
      const clampedY = Math.max(pyMin, Math.min(pyMax, b.y));
      const dy = b.y - clampedY;
      const sumR = b.radius + radius;
      const dist2 = ds * ds + dl * dl + dy * dy;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      const nl = dist > 0.001 ? -dl / dist : 0;
      const ns = dist > 0.001 ? -ds / dist : -1;
      return {
        normalS: ns,
        normalL: nl,
        penetration: sumR - dist,
        consume: () => {
          group.remove(b.mesh);
          const idx = balls.indexOf(b);
          if (idx >= 0) balls.splice(idx, 1);
        },
      };
    }
    return null;
  };

  /** Spinner-bar contact for rolling-tubes. Same maths + return shape
   *  as circle-spinner's barContact so Game can reuse the impulse path. */
  const barContact = (s: number, l: number, radius: number) => {
    for (const sp of spinners) {
      const dl = l;
      const ds = s - sp.s;
      const cos = Math.cos(sp.angle);
      const sin = Math.sin(sp.angle);
      const localX = dl * cos - ds * sin;
      const localY = dl * sin + ds * cos;
      const halfLen = sp.halfLength;
      const halfThick = sp.thickness;
      const clampedX = Math.max(-halfLen, Math.min(halfLen, localX));
      const dxLocal = localX - clampedX;
      const dyLocal = localY;
      const dist2 = dxLocal * dxLocal + dyLocal * dyLocal;
      const sumR = halfThick + radius;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      const nxLocal = dist > 0.001 ? dxLocal / dist : 0;
      const nyLocal = dist > 0.001 ? dyLocal / dist : 1;
      const normalL = nxLocal * cos + nyLocal * sin;
      const normalS = -nxLocal * sin + nyLocal * cos;
      const barVelL = sp.angularVel * ds;
      const barVelS = -sp.angularVel * dl;
      return { normalS, normalL, penetration: sumR - dist, barVelS, barVelL };
    }
    return null;
  };

  /** Hard forward block: the player is blocked only when the blade is
   *  CURRENTLY in their way. As the axe swings up to the side, the gap
   *  underneath opens and the block releases — the player can run
   *  through during that window. Block resumes when the blade swings
   *  back down. Returns the s-clamp value or null if currently clear. */
  const axeForwardBlock = (s: number, worldY: number): number | null => {
    // Approximate player capsule height — head sits ~1.6 m above feet.
    const PLAYER_HEIGHT = 1.6;
    const playerHeadY = worldY + PLAYER_HEIGHT;
    for (const a of axes) {
      // Approach window: 1.2 m before the blade through 0.05 m after.
      if (s < a.pivotS - 1.2) continue;
      if (s > a.pivotS + 0.05) continue;
      // Current blade Y based on the actual swing angle, NOT the worst
      // case. When the rope is tilted, the blade rises. cos(angle) = 1 at
      // hang-straight-down (lowest), cos(angle) < 1 as it swings.
      const cosA = Math.cos(a.angle);
      const bladeCenterY = a.pivotY - cosA * a.rope;
      const bladeBottomY = bladeCenterY - a.bladeHalfHeight;
      // If the blade has swung high enough that its bottom is above the
      // player's head, the player can run under cleanly — no block.
      if (playerHeadY < bladeBottomY - 0.05) continue;
      // If the player jumped high enough to clear the blade top, no block.
      const bladeTopY = bladeCenterY + a.bladeHalfHeight;
      if (worldY >= bladeTopY + 0.05) continue;
      return a.pivotS - 0.6;
    }
    return null;
  };

  /** Axe-blade vs player capsule contact. Same convention as maze-rush's
   *  pendulumContact: returns normalL/normalY and a lateral barVelL the
   *  Game uses to set knockback strength. */
  const axeContact = (s: number, l: number, worldY: number, radius: number) => {
    const PLAYER_CHEST_OFFSET = 0.55;
    const PAD = 0.32;
    const pY = worldY + PLAYER_CHEST_OFFSET;
    for (const a of axes) {
      // Skip if player is far along s.
      const ds = s - a.pivotS;
      if (Math.abs(ds) > a.bladeHalfHeight + radius + PAD + 0.5) continue;
      // Blade centre in world: pivot + rope rotated by angle around Z.
      const sinA = Math.sin(a.angle);
      const cosA = Math.cos(a.angle);
      const Cx = sinA * a.rope;
      const Cy = a.pivotY - cosA * a.rope;
      const dx = l - Cx;
      const dy = pY - Cy;
      const dz = ds;
      // Treat the blade as an axis-aligned box (good enough at low angles).
      const ex = Math.max(0, Math.abs(dx) - a.bladeHalfWidth);
      const ey = Math.max(0, Math.abs(dy) - a.bladeHalfHeight);
      const ez = Math.max(0, Math.abs(dz) - 0.18);
      const dist = Math.sqrt(ex * ex + ey * ey + ez * ez);
      const sumR = radius + PAD;
      if (dist >= sumR) continue;
      const safeDx = dx || (Math.sign(dx) || 1) * 0.001;
      const nl = Math.sign(safeDx);
      const ny = dy >= 0 ? 1 : -1;
      const barVelL = Math.sign(nl) * a.amp * a.freq * a.rope;
      return { normalL: nl, normalY: ny, penetration: sumR - dist, barVelL };
    }
    return null;
  };

  // Now that the tubes are flat boxes, their top mesh is a solid standable
  // surface. Add them to supportMeshes so the raycaster catches them and the
  // player's y stays locked to the top without the cylinder-curvature
  // jitter that the old design had.
  for (const tm of tubeMeshes) supportMeshes.push(tm.topMesh);

  const tubeAt = (s: number) => {
    for (const t of rt.tubes) {
      if (s >= t.start && s <= t.end) {
        return {
          radius: t.radius,
          angularVel: t.angularVel,
          // Half-width of the walkable top. Slightly inset from the visible
          // edge so the player falls off just before sliding into the wall.
          topHalfWidth: rt.halfWidth * 0.78,
        };
      }
    }
    return null;
  };

  return { group, supportMeshes, gatePositions, tick, tubeAt, axeContact, axeForwardBlock, barContact, ballContact };
}

/** Swinging axe pendulum: a chain hanging from the pivot with a fat blade
 *  at the bottom. The whole group rotates around Z so the blade swings
 *  laterally across the platform. Sized via opts so we can mount a
 *  full-width "tube guillotine" or a smaller platform axe with the same
 *  builder. */
function buildAxePendulum(opts: { rope: number; bladeWidth: number; bladeHeight: number }): THREE.Group {
  const g = new THREE.Group();
  const chain = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.08, opts.rope, 8),
    new THREE.MeshStandardMaterial({ color: 0xb0b3c0, roughness: 0.4, metalness: 0.45 }),
  );
  chain.position.y = -opts.rope / 2;
  g.add(chain);
  const blade = new THREE.Mesh(
    new SoftBox(opts.bladeWidth, opts.bladeHeight, 0.36),
    new THREE.MeshStandardMaterial({
      color: 0xd2d8e2,
      emissive: 0x8a93a8,
      emissiveIntensity: 0.18,
      roughness: 0.35,
      metalness: 0.6,
    }),
  );
  blade.position.y = -opts.rope - opts.bladeHeight / 2 + 0.15;
  blade.castShadow = true;
  g.add(blade);
  // Red trim along the bottom edge for visual punch.
  const trim = new THREE.Mesh(
    new SoftBox(opts.bladeWidth + 0.06, 0.26, 0.42),
    new THREE.MeshStandardMaterial({ color: 0xef476f, roughness: 0.4 }),
  );
  trim.position.y = -opts.rope - opts.bladeHeight + 0.06;
  g.add(trim);
  return g;
}

/** Yellow gantry frame above the platform — the axe hangs from its centre. */
function buildAxeGantry(pivotY: number, pivotS: number, trackWidth: number): THREE.Group {
  const g = new THREE.Group();
  const postMat = new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.45, metalness: 0.25 });
  const POST_H = pivotY + 1.2;
  for (const sx of [-trackWidth * 0.45, trackWidth * 0.45]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.24, POST_H, 12), postMat);
    post.position.set(sx, POST_H / 2, pivotS);
    g.add(post);
  }
  const beam = new THREE.Mesh(new SoftBox(trackWidth * 0.95, 0.36, 0.36), postMat);
  beam.position.set(0, POST_H + 0.08, pivotS);
  g.add(beam);
  return g;
}
